import type { FC, ReactNode } from 'react';
import { useCallback } from 'react';

import { useGameConfig } from '../../../config/GameConfigProvider';
import { CurrencyFormatter } from '../../../formatters/CurrencyFormatter';
import { useHideCurrency } from '../../../store/compositeSelectors/useHideCurrency';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useSettingsSlice } from '../../../store/slices/settings/selectors';
import { toastActions } from '../../../store/slices/toast/slice';
import { ChipConnected } from '../../game/chip/ChipConnected';
import { Toast } from '../../toast/Toast';
import type { ToastConnectedProps } from '../types';

export const MinBetToastConnected: FC<ToastConnectedProps> = ({ message, timeStamp, ...rest }): ReactNode => {
  const dispatch = useCoreDispatch();
  const tableConfig = useConfigSlice.tableConfig();
  const minBet = tableConfig?.tableBetLimit.min;
  const currency = useGameStateSlice.getCurrency();
  const currentLanguage = useSettingsSlice.currentLanguage();
  const hideCurrency = useHideCurrency();
  const isChipless = Boolean(useGameConfig().config.isChipless);

  const currencyValue = minBet ?? 0;
  const iconChip = isChipless ? undefined : <ChipConnected size={24} value={minBet ?? 1} weight={1} />;

  const closeFn = useCallback(() => {
    dispatch(toastActions.removeToast(timeStamp));
  }, [dispatch, timeStamp]);

  if (!currency) return null;

  const formattedValue = CurrencyFormatter.format(currencyValue, currentLanguage, { currency, hideCurrency });

  return (
    <Toast
      message={message}
      msgDynamicValues={[formattedValue]}
      onClose={closeFn}
      icon={iconChip}
      {...rest}
      duration={0}
    />
  );
};
